import { useState } from 'react';
import dayjs from 'dayjs';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Avatar,
    Button,
    Chip, Divider
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Home,
    Schedule,
    LocationOn,
    AttachMoney,
    AccessTime,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useJobEmployeeAccept } from './hooks/useJobEmployeeAccept';

function JobEmployeeAccept({ data }) {

    const { uid } = useSelector((state) => state.userState)

    const { handleAcceptOrDecline } = useJobEmployeeAccept()
    const currentEmployee = data?.staff?.find((employee) => employee.uid === uid)

    const jobInfo = data || {};

    const startDate = dayjs(jobInfo.startDate);
    const endDate = dayjs(jobInfo.endDate);

    const formatDate = (date) => date.format('dddd, MMMM D, YYYY');
    const formatTime = (startDate, endDate) =>
        `${startDate.format('h:mm A')} - ${endDate.format('h:mm A')}`;

    const durationHours = endDate.diff(startDate, 'hour');

    const workScope = jobInfo.workScope || {};
    const workType = workScope.workType?.name || 'Cleaning Service';
    const quotes = workScope.quotes?.[0] || {};
    const workScopeServices = quotes.workScopeServices?.[0] || {};
    const serviceName = workScopeServices.workService?.name || 'General Cleaning';
    const squareFt = workScopeServices.squareFt || 0;

    const staffRates = quotes.staffRatePerHour || [];
    const employeeRate =
        staffRates.find((rate) => rate.type === 'employee')?.rate || 0;
    const totalPay = employeeRate * durationHours;

    const client = jobInfo.client || {};
    const clientName =
        `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Client';
    const clientCompany = client.clientCompanyName || '';

    const location = jobInfo.coverageArea || {};

    const lat = location.latitude
    const lng = location.longitude

    const address = location.address || location.name || 'Address not specified';
    const city = location.city || '';
    const state = location.state || '';

    const company = jobInfo.company || {};
    const companyName = company.businessName || 'Company';

    const [status, setStatus] = useState(currentEmployee?.StaffByJob?.status);
    const [isLoading, setIsLoading] = useState(false);

    const handleAccept = async () => {
        setIsLoading(true);
            handleAcceptOrDecline(jobInfo.id, "approved")
            setStatus("approved")
            setIsLoading(false);
        
    }

    const handleDecline = async () => {
        setIsLoading(true);
        setTimeout(() => {
            handleAcceptOrDecline(jobInfo.id, "declined")
            setStatus("declined")
            setIsLoading(false);
        }, 1200);
    };

    console.log(status);

    if (status === "approved" || status === "declined") {
        return (
            <Box
                sx={{
                    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                    p: { xs: 1, sm: 3 },
                    width: '100%',
                    minHeight: '400px',
                    mx: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 2, sm: 4 },
                        backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                        minHeight: '340px',
                        width: "100%",
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                    }}
                >
                    {status === 'approved' && (
                        <>
                            <CheckCircle
                                sx={{
                                    fontSize: { xs: 48, sm: 80 },
                                    color: 'success.main',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color="success.main"
                                gutterBottom
                                sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                            >
                                Job Accepted!
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                You've successfully accepted this cleaning job. The business has been notified.
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: 'primary.50',
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 2,
                                    mt: 3,
                                    width: '100%',
                                    textAlign: { xs: 'center', sm: 'left' },
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" color="primary.main" gutterBottom>
                                    Next Steps:
                                </Typography>
                                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • Contact client: {client.phone || 'Phone not provided'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • Arrive on time: {formatTime(startDate, endDate)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • Service: {serviceName}
                                    </Typography>
                                    <Typography variant="body2">
                                        • Complete job and mark as finished in app
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                    {status === "declined" && (
                        <>
                            <Cancel
                                sx={{
                                    fontSize: { xs: 48, sm: 80 },
                                    color: 'error.main',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color="error.main"
                                gutterBottom
                                sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
                            >
                                Job Declined
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                You've declined this cleaning job. The business will be notified and the job will be reassigned to another cleaner.
                            </Typography>
                        </>
                    )
                    }
                </Paper>
            </Box>
        );
    }

    // MAIN SCREEN
    return (
        <Box
            sx={{
                p: { xs: 1, sm: 2 },
                width: '100%',
                mx: 'auto',
                backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 1.5, sm: 3 },
                    bgcolor: 'background.default',
                    width: '100%',
                    maxWidth: { xs: '100%', md: 950 },
                    mx: 'auto',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        textAlign: 'center',
                        bgcolor: 'var(--fx-surface-base, #FFFFFF)',
                        py: { xs: 2, sm: 3 },
                        mb: 3,
                        borderRadius: { xs: 1, sm: 2 },
                        border: '2px solid #e3f2fd',
                    }}
                >
                    <Home
                        sx={{
                            fontSize: { xs: 32, sm: 40, md: 50 },
                            color: 'primary.main',
                            mb: 1,
                            display: 'block',
                            mx: 'auto',
                        }}
                    />
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ fontSize: { xs: '1.6rem', sm: '2.2rem' } }}
                    >
                        New {workType} Job
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                    >
                        Review the details and choose to accept or decline
                    </Typography>
                </Box>

                {/* Job Info Grid */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Left Column - Job Details */}
                    <Grid item xs={12} sm={12} md={8}>
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
                            >
                                {serviceName}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mb: 3,
                                    width: '100%',
                                }}
                            >
                                <Chip
                                    icon={<LocationOn />}
                                    label={`${city}, ${state}`}
                                    variant="outlined"
                                    size="medium"
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                />
                                <Chip
                                    icon={<Schedule />}
                                    label={`${formatDate(startDate)} • ${formatTime(startDate, endDate)}`}
                                    variant="outlined"
                                    size="medium"
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                />
                                {employeeRate > 0 && (
                                    <Chip
                                        icon={<AttachMoney />}
                                        label={`$${employeeRate}/hour`}
                                        variant="outlined"
                                        size="medium"
                                        color="success"
                                        sx={{
                                            width: { xs: '100%', sm: 'auto' },
                                            fontWeight: 'bold',
                                            borderColor: 'success.main',
                                            color: 'success.main'
                                        }}
                                    />
                                )}
                                {employeeRate > 0 && totalPay > 0 && (
                                    <Chip
                                        icon={<AccessTime />}
                                        label={`Total: $${totalPay.toFixed(2)}`}
                                        variant="filled"
                                        size="medium"
                                        color="success"
                                        sx={{
                                            width: { xs: '100%', sm: 'auto' },
                                            fontWeight: 'bold',
                                            backgroundColor: 'success.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'success.dark'
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Service Details:
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    {squareFt > 0
                                        ? `Area to clean: ${squareFt.toLocaleString()} sq ft`
                                        : 'Area details not specified'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    Duration: {durationHours} hour{durationHours !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Location:
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    {address}
                                </Typography>

                                {lat && lng && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            width: '100%',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: 1,
                                            minHeight: { xs: 200, sm: 300 },
                                            height: { xs: 220, sm: 300 }
                                        }}
                                    >
                                        <iframe
                                            title="Google Map"
                                            width="100%"
                                            height="100%"
                                            style={{ border: 0 }}
                                            loading="lazy"
                                            allowFullScreen
                                            referrerPolicy="no-referrer-when-downgrade"
                                            src={`https://www.google.com/maps?q=${lat},${lng}&z=16&output=embed`}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                    {/* Right Column - Client & Payment Info */}
                    <Grid item xs={12} sm={12} md={4}>
                        {/* Client Info */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 1.2, sm: 2 },
                                mb: 3,
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                            >
                                Client Information
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Avatar
                                    src={client.avatar}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        mr: 2,
                                        bgcolor: 'primary.light',
                                        fontSize: { xs: 18, sm: 20 },
                                    }}
                                >
                                    {clientName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                                    >
                                        {clientName}
                                    </Typography>
                                    {clientCompany && (
                                        <Typography variant="body2" color="text.secondary">
                                            {clientCompany}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        {client.phone || 'Phone not provided'}
                                    </Typography>
                                </Box>
                            </Box>

                        </Paper>
                        {/* Company Info */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 1.2, sm: 2 },
                                mt: 3,
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                            >
                                Assigned by Company
                            </Typography>
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                            >
                                {companyName}
                            </Typography>
                            {company.businessPhone && (
                                <Typography variant="body2" color="text.secondary">
                                    {company.businessPhone}
                                </Typography>
                            )}
                        </Paper>
                        {/* Payment Info */}
                        <Paper
                            elevation={1}
                            sx={{
                                p: { xs: 1.2, sm: 2 },
                                mt: 3,
                                bgcolor: 'success.50',
                                borderColor: 'success.200',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                                color="success.dark"
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                            >
                                Payment Details
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Hourly Rate:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    ${employeeRate}/hour
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Duration:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {durationHours} hour{durationHours !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" fontWeight="bold">
                                    Estimated Pay:
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.dark">
                                    ${totalPay.toFixed(2)}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                {currentEmployee?.StaffByJob?.jobUserRole === "contractor" && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: 2, sm: 3 },
                            justifyContent: 'center',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'stretch',
                            mt: 4,
                            width: '100%',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="error"
                            size="large"
                            startIcon={<Cancel />}
                            onClick={handleDecline}
                            disabled={isLoading}
                            sx={{
                                minWidth: { xs: '100%', sm: 160 },
                                height: 56,
                                fontSize: '1.1rem',
                                width: { xs: '100%', sm: 'auto' },
                                mb: { xs: 1.5, sm: 0 },
                                '&:hover': { borderWidth: 2 },
                            }}
                        >
                            {isLoading ? 'Processing...' : 'Decline Job'}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            size="large"
                            startIcon={<CheckCircle />}
                            onClick={handleAccept}
                            disabled={isLoading}
                            sx={{
                                minWidth: { xs: '100%', sm: 160 },
                                height: 56,
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                width: { xs: '100%', sm: 'auto' },
                                boxShadow: 3,
                                '&:hover': { boxShadow: 6 },
                            }}
                        >
                            {isLoading ? 'Processing...' : 'Accept Job'}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
export default JobEmployeeAccept;